// client.ts — jedyna warstwa, przez którą Control Center rozmawia z backendami.
//
// Dlaczego abstrakcja, a nie gołe fetch() w komponentach:
//  1) decyzja DIRECT_MULTI_API vs BFF (ADR-002) ma być zmianą JEDNEGO pliku, nie stu komponentów;
//  2) token nie może wyciekać do przypadkowych miejsc — jest wstrzykiwany tutaj;
//  3) każdy błąd ma być klasyfikowalny (patrz errors.ts), a nie „coś poszło nie tak”;
//  4) R5-15-001 jest read-only — bezpiecznik siedzi w jednym miejscu i da się go przetestować.

import { config, type ApiSystem } from '@/config/env';
import { ApiError, kindFromStatus } from './errors';
import { zapiszTrybZOdpowiedzi } from '@/lib/trybZrodla';
import { naControlCenter } from './trasowanie';

export type TokenProvider = (system: ApiSystem) => string | null;

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export interface RequestOptions {
  method?: string;
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Nagłówki dodatkowe. Authorization jest ustawiane wyłącznie przez klienta. */
  headers?: Record<string, string>;
}

export interface ApiClientDeps {
  fetchImpl?: typeof fetch;
  getToken?: TokenProvider;
  /** Nadpisanie bazowych adresów — używane w testach. */
  apiBase?: Partial<Record<ApiSystem, string>>;
  readOnly?: boolean;
}

export function buildUrl(base: string, path: string, query?: RequestOptions['query']): string {
  const joined = `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  if (!query) return joined;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    params.append(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${joined}?${qs}` : joined;
}

export class ApiClient {
  private readonly fetchImpl: typeof fetch;
  private readonly getToken: TokenProvider;
  private readonly apiBase: Record<ApiSystem, string>;
  private readonly readOnly: boolean;

  constructor(deps: ApiClientDeps = {}) {
    this.fetchImpl = deps.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.getToken = deps.getToken ?? (() => null);
    this.apiBase = { ...config.apiBase, ...(deps.apiBase ?? {}) };
    this.readOnly = deps.readOnly ?? config.readOnly;
  }

  /** Adres, pod który REALNIE poleci zapytanie. Ekran diagnostyczny musi pokazywać to, a nie config. */
  baseFor(system: ApiSystem): string {
    return this.apiBase[system];
  }

  async request<T>(system: ApiSystem, path: string, options: RequestOptions = {}): Promise<T> {
    const method = (options.method ?? 'GET').toUpperCase();

    // Bezpiecznik R5-15-001: żadnego zapisu do żadnego backendu, dopóki nie ma AUDIT PASS.
    if (this.readOnly && MUTATING.has(method)) {
      throw new ApiError({
        kind: 'client',
        message: `Control Center jest w trybie read-only — ${method} ${path} zablokowane po stronie klienta.`,
        url: path,
      });
    }

    // JEDNO WEJŚCIE. Ekran mówi, o które źródło pyta, ale pyta zawsze Control Center —
    // przekład ścieżki siedzi w `trasowanie.ts` i jest jedynym miejscem, które o tym wie.
    const url = buildUrl(this.apiBase[system], naControlCenter(path), options.query);
    const headers: Record<string, string> = { Accept: 'application/json', ...(options.headers ?? {}) };

    // ŻADNEGO TOKENA W NAGŁÓWKU. Sesja Control Center jedzie ciasteczkiem httpOnly, którego
    // skrypt strony nie czyta i nie może zgubić. `getToken` zostaje w zależnościach wyłącznie
    // dla testów kontraktu klienta; w aplikacji nikt go nie podaje.
    const token = this.getToken(system);
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';

    const controller = new AbortController();
    const timeoutMs = options.timeoutMs ?? 20_000;
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    if (options.signal) {
      options.signal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        method,
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: controller.signal,
        // Ciasteczko sesji Control Center. `include`, a nie `same-origin`: gdy front stoi pod
        // innym pochodzeniem niż backend (osobna usługa za tą samą bramą), `same-origin` po
        // cichu NIE dołączyłby sesji i każde żądanie wracałoby 401 bez śladu, dlaczego.
        credentials: 'include',
      });
    } catch (cause) {
      throw new ApiError({
        kind: 'network',
        message: cause instanceof Error ? cause.message : 'network error',
        url,
      });
    } finally {
      clearTimeout(timer);
    }

    const raw = await response.text();
    let parsed: unknown = null;
    if (raw.length > 0) {
      try {
        parsed = JSON.parse(raw);
      } catch {
        if (response.ok) {
          throw new ApiError({ kind: 'malformed', message: 'Odpowiedź nie jest JSON-em', status: response.status, url });
        }
      }
    }

    if (!response.ok) {
      throw new ApiError({
        kind: kindFromStatus(response.status),
        message: `HTTP ${response.status} ${method} ${path}`,
        status: response.status,
        url,
        body: parsed,
      });
    }

    // Tryb zrodla jedzie w KAZDEJ odpowiedzi analityki. Zapisujemy go tutaj, w jednym miejscu,
    // zeby zadne wywolanie nie moglo o tym zapomniec — a zdanie „skad sa te liczby" pochodzilo
    // z tego, z czym aplikacja rozmawia, a nie z tego, jak ja zbudowano.
    zapiszTrybZOdpowiedzi(parsed);

    return parsed as T;
  }

  get<T>(system: ApiSystem, path: string, query?: RequestOptions['query'], options: RequestOptions = {}): Promise<T> {
    return this.request<T>(system, path, { ...options, method: 'GET', query });
  }

  /**
   * Wyjątek od bezpiecznika read-only: cykl życia SESJI (login/logout).
   * To nie jest zapis danych domenowych — bez tego nie da się w ogóle uwierzytelnić.
   * Ścieżki są na sztywnej liście, żeby „wyjątek na logowanie” nie stał się furtką na wszystko.
   */
  sessionRequest<T>(system: ApiSystem, path: string, options: RequestOptions = {}): Promise<T> {
    if (!SESSION_PATHS.has(path)) {
      throw new ApiError({
        kind: 'client',
        message: `Ścieżka ${path} nie jest endpointem sesji — użyj request().`,
        url: path,
      });
    }
    return this.zZdjetymBezpiecznikiem<T>(system, path, options);
  }

  /**
   * DZIAŁANIA OPERACYJNE — wąska, jawna lista, lustro tej po stronie serwera.
   *
   * ┌─ DLACZEGO OSOBNA METODA, A NIE ZDJĘCIE BEZPIECZNIKA ──────────────────────────────────────┐
   * │ Bezpiecznik `readOnly` blokuje KAŻDY zapis i to jest jego wartość. Gdyby ekran mógł go    │
   * │ zdjąć „na chwilę", przestałby cokolwiek gwarantować — a pierwszy nowy ekran zrobiłby to   │
   * │ dla wygody.                                                                                │
   * │                                                                                            │
   * │ Zamiast tego lista ścieżek: dopisanie się do niej jest widoczne w przeglądzie, tak samo    │
   * │ jak dopisanie się do listy po stronie serwera. Serwer i tak rozstrzyga — ta lista chroni   │
   * │ przed przypadkowym wysłaniem, nie zastępuje kontroli.                                      │
   * └────────────────────────────────────────────────────────────────────────────────────────────┘
   */
  dzialanieOperacyjne<T>(system: ApiSystem, path: string, options: RequestOptions = {}): Promise<T> {
    if (!DZIALANIA_OPERACYJNE.has(path)) {
      throw new ApiError({
        kind: 'client',
        message: `Ścieżka ${path} nie jest autoryzowanym działaniem operacyjnym.`,
        url: path,
      });
    }
    return this.zZdjetymBezpiecznikiem<T>(system, path, options);
  }

  /** Wspólny mechanizm obu wyjątków. Jedno miejsce, w którym bezpiecznik jest omijany. */
  private zZdjetymBezpiecznikiem<T>(
    system: ApiSystem, path: string, options: RequestOptions,
  ): Promise<T> {
    const bypass = new ApiClient({
      fetchImpl: this.fetchImpl,
      getToken: this.getToken,
      apiBase: this.apiBase,
      readOnly: false,
    });
    return bypass.request<T>(system, path, options);
  }
}

/**
 * Jedyne ścieżki, na których wolno wykonać metodę zapisującą.
 *
 * Lista skurczyła się z trzech pozycji do dwóch i JEST TO MIARA ZMIANY: dopóki przeglądarka
 * logowała się osobno do CRM i osobno do Drive, były tu trzy wejścia przyjmujące hasło.
 * Zostało jedno logowanie i jedno wylogowanie, oba do Control Center.
 *
 * Logowanie i wylogowanie są POST-ami, ale nie są ZAPISEM DANYCH — dlatego mają tu wyjątek,
 * a nie dlatego, że tryb tylko do odczytu bywa niewygodny.
 */
const SESSION_PATHS: ReadonlySet<string> = new Set([
  '/api/cc/auth/login',
  '/api/cc/auth/logout',
  /*
   * Wejście właściciela w panel restauracji: login RESTAURACJI wskazuje, czyje dane,
   * a hasło WŁAŚCICIELA dowodzi, że wolno. To jest logowanie, nie zapis danych — stąd tutaj,
   * a nie na liście działań operacyjnych.
   *
   * Lustro `SCIEZKI_SESJI` po stronie serwera. Rozjazd tych dwóch list znaczy formularz,
   * który odbija się od własnego bezpiecznika, zanim dojdzie do backendu.
   */
  '/api/cc/restauracja/logowanie-mistrza',
]);

/**
 * DZIAŁANIA OPERACYJNE — lustro listy z `server/middleware/straznicy.js`.
 *
 * Wejście i zakończenie podglądu restauracji są POST-ami, bo niosą hasło potwierdzenia.
 * Hasło w adresie zostałoby w historii przeglądarki, w nagłówku `Referer` i w dziennikach
 * serwerów pośredniczących — z żadnego z tych miejsc nie da się go wycofać.
 *
 * Ta lista NIE jest zabezpieczeniem — rozstrzyga serwer. Chroni przed przypadkowym wysłaniem
 * i sprawia, że dopisanie nowego zapisu jest widoczne w przeglądzie po obu stronach.
 */
const DZIALANIA_OPERACYJNE: ReadonlySet<string> = new Set([
  '/api/cc/admin/podglad/wejdz',
  '/api/cc/admin/podglad/zakoncz',
]);
